import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link2Off } from "lucide-react";
import { type Copia, ErroreCopia, linkApi } from "@/api/linkPreventivo";
import { dataBreve, totaleInCima } from "@/lib/linkPreventivo";
import { nomiDellaVista } from "@/lib/preventivoFormato";
import { AvvisoErrore } from "@/components/preventivo/BolletteFuori";
import { SchedaCondominio } from "@/components/preventivo/SchedaCondominio";
import { TabellaCondomini } from "@/components/preventivo/TabellaCondomini";
import { RiquadroTuttiLettura } from "@/components/preventivo/TuttiCondomini";
import { ModoPreventivo } from "@/components/preventivo/modo";
import { COLLEGAMENTO_NON_RIUSCITO, conteggio } from "@/components/preventivo/scritte";
import { LINK_SCADUTO, preparato } from "@/components/preventivo/scritteLink";

type Stato = { tipo: "caricamento" } | { tipo: "pronta"; vista: Copia } | { tipo: "scaduto" } | { tipo: "errore" };

/** I token dei file valgono 15 minuti: la copia si rilegge ogni 10, e al ritorno sulla pagina. */
const RILETTURA_MS = 10 * 60_000;

const niente = () => undefined;

/** Meta della pagina finché è aperta: non si indicizza e chi la apre da qui non riceve l'indirizzo (col token). */
function useMetaPrivate() {
  useEffect(() => {
    const metti = (name: string, content: string) => {
      const m = document.createElement("meta");
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    };
    const robots = metti("robots", "noindex, nofollow");
    const referrer = metti("referrer", "no-referrer");
    const titolo = document.title;
    document.title = "Preventivo condomini";
    return () => {
      robots.remove();
      referrer.remove();
      document.title = titolo;
    };
  }, []);
}

/** Testata della pagina pubblica: il marchio, nessun menù (l'amministratore non entra nel portale). */
function Testata() {
  return (
    <header className="bg-sidebar px-4 py-3 md:px-6">
      <img src="/logo.png" alt="AIEnergia" className="h-7 brightness-0 invert" />
    </header>
  );
}

/** La copia ferma per l'amministratore (`/p/#<token>`, spec § 8): la stessa tabella e le stesse schede dell'operatore,
 *  in sola lettura, coi file del link. Il token sta nel frammento (non arriva a nessun server) e va solo
 *  nell'intestazione della richiesta. Scaduta, spenta, manomessa o mai esistita: «Link scaduto». */
export function CopiaPreventivo() {
  const token = useLocation().hash.replace(/^#/, "");
  const [stato, setStato] = useState<Stato>(token ? { tipo: "caricamento" } : { tipo: "scaduto" });
  useMetaPrivate();

  // ogni «Riprova» rilegge; una risposta arrivata dopo l'uscita dalla pagina non conta
  const [tentativo, setTentativo] = useState(0);
  useEffect(() => {
    if (!token) return;
    let viva = true;
    linkApi.copia(token).then(
      (v) => { if (viva) setStato({ tipo: "pronta", vista: nomiDellaVista(v) as Copia }); },
      (e) => { if (viva) setStato(e instanceof ErroreCopia && e.scaduto ? { tipo: "scaduto" } : { tipo: "errore" }); },
    );
    return () => { viva = false; };
  }, [token, tentativo]);

  // a pagina aperta i token dei file si rinnovano da sé: ogni 10 minuti e quando si torna sulla scheda. Un guasto qui
  // non cambia la pagina (i token di prima valgono ancora); un link scaduto nel frattempo sì.
  const pronta = stato.tipo === "pronta";
  useEffect(() => {
    if (!pronta) return;
    const rinnova = () => {
      linkApi.copia(token).then(
        (v) => setStato({ tipo: "pronta", vista: nomiDellaVista(v) as Copia }),
        (e) => { if (e instanceof ErroreCopia && e.scaduto) setStato({ tipo: "scaduto" }); },
      );
    };
    const t = setInterval(rinnova, RILETTURA_MS);
    const visibile = () => { if (document.visibilityState === "visible") rinnova(); };
    document.addEventListener("visibilitychange", visibile);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", visibile); };
  }, [pronta, token]);

  const file = stato.tipo === "pronta" ? stato.vista.file : null;
  const modo = useMemo(() => ({
    solaLettura: true,
    fileUrl: (id: string) => (file?.[id] ? linkApi.fileUrl(file[id]) : null),
  }), [file]);

  return (
    <div className="min-h-dvh overflow-x-hidden bg-surface">
      <Testata />
      <main className="mx-auto max-w-7xl p-4 md:p-6">
        {stato.tipo === "caricamento" && (
          <div aria-busy="true" className="h-24 rounded-lg border border-border bg-card animate-pulse motion-reduce:animate-none" />
        )}
        {stato.tipo === "scaduto" && (
          <div className="mx-auto mt-6 flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card px-6 py-10 text-center">
            <Link2Off size={28} aria-hidden="true" className="text-muted-foreground" />
            <h1 className="font-heading text-xl font-semibold text-text">{LINK_SCADUTO}</h1>
          </div>
        )}
        {stato.tipo === "errore" && (
          <div className="rounded-lg border border-border bg-card p-3">
            <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={() => { setStato({ tipo: "caricamento" }); setTentativo((t) => t + 1); }} />
          </div>
        )}
        {stato.tipo === "pronta" && (
          <ModoPreventivo.Provider value={modo}>
            <div className="space-y-5">
              <div>
                <h1 className="text-lg font-semibold text-text">Preventivo condomini</h1>
                <p className="text-sm text-muted-foreground">
                  {preparato(stato.vista.preparato_da, dataBreve(`${stato.vista.preparato_il}T12:00:00Z`))}
                </p>
                {/* col totale in cima il conteggio lo dice già il totale (giro 4) */}
                {!totaleInCima(stato.vista.condomini) && (
                  <p className="text-sm text-muted-foreground tabular-nums">
                    {conteggio(stato.vista.totale, stato.vista.condomini.length)}
                  </p>
                )}
              </div>
              {/* il totale dei condomìni della copia, in cima e a tutte le larghezze: l'amministratore non sceglie nulla,
                  quindi niente barra fissa in basso (Alfonso, 24/10) */}
              <RiquadroTuttiLettura condomini={stato.vista.condomini} />
              {/* sotto 640 px una scheda per condominio, da 640 px la tabella unica (compatta fino a 1023), come la pagina dell'operatore */}
              {stato.vista.condomini.map((c) => (
                <SchedaCondominio key={c.chiave} className="sm:hidden" condominio={c} offerte={stato.vista.offerte}
                  onCambia={niente} occupato={false} onScegliContatore={niente} onElimina={niente}
                  avvisoRiga={() => null} />
              ))}
              <TabellaCondomini
                className="hidden sm:block"
                condomini={stato.vista.condomini}
                offerte={stato.vista.offerte}
                occupato={false}
                aperto={null}
                onApriCondominio={niente}
                onChiudi={niente}
                onScegli={niente}
                onScegliContatore={niente}
                onElimina={niente}
                avvisoCondominio={() => null}
                avvisoRiga={() => null}
              />
            </div>
          </ModoPreventivo.Provider>
        )}
      </main>
    </div>
  );
}
